import React, { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import './RichTextEditor.css';

interface Props {
  content: string;
  onChange: (html: string) => void;
}

function ToolbarButton({
  onClick,
  active,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={`rte-toolbar-btn${active ? ' active' : ''}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function RichTextEditor({ content, onChange }: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      Image,
      Placeholder.configure({ placeholder: 'Start writing...' }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  const [showHtmlInput, setShowHtmlInput] = useState(false);
  const [htmlInput, setHtmlInput] = useState('');

  if (!editor) return null;

  const importHtml = () => {
    if (htmlInput.trim()) {
      editor.commands.setContent(htmlInput.trim());
      onChange(editor.getHTML());
    }
    setHtmlInput('');
    setShowHtmlInput(false);
  };

  const addImage = () => {
    const url = window.prompt('Image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const addLink = () => {
    const url = window.prompt('Link URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div className="rte-wrapper">
      <div className="rte-toolbar">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
        >
          B
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
        >
          I
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
        >
          H2
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive('heading', { level: 3 })}
        >
          H3
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
        >
          List
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
        >
          1.
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive('blockquote')}
        >
          &ldquo;
        </ToolbarButton>
        <ToolbarButton onClick={addLink} active={editor.isActive('link')}>
          Link
        </ToolbarButton>
        <ToolbarButton onClick={addImage}>
          Img
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          &mdash;
        </ToolbarButton>
        <div className="rte-toolbar-separator" />
        <ToolbarButton onClick={() => setShowHtmlInput(!showHtmlInput)}>
          HTML
        </ToolbarButton>
      </div>
      {showHtmlInput && (
        <div className="rte-html-import">
          <textarea
            className="rte-html-textarea"
            value={htmlInput}
            onChange={e => setHtmlInput(e.target.value)}
            placeholder="Paste raw HTML here..."
            rows={6}
          />
          <div className="rte-html-actions">
            <button type="button" className="rte-toolbar-btn" onClick={() => setShowHtmlInput(false)}>
              Cancel
            </button>
            <button type="button" className="rte-toolbar-btn active" onClick={importHtml}>
              Import
            </button>
          </div>
        </div>
      )}
      <EditorContent editor={editor} className="rte-content" />
    </div>
  );
}

export default RichTextEditor;
